import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthProvider } from '@contexts/AuthContext';
import { GameSettingsProvider } from '@contexts/GameSettingsContext';
import './App.css';
import InstallBanner from '@ui/InstallBanner';

const Login = lazy(() => import('@screens/Login/Login'));
const Signup = lazy(() => import('@screens/Signup/Signup'));
const TechGate = lazy(() => import('@screens/TechSpec/TechGate'));
const Feedback = lazy(() => import('@screens/Feedback/Feedback'));
// 운영자 전용 부하 시험. lazy 라 안 들어가면 번들을 안 받는다 — 게임 로딩에 얹히지 않는다.
const AdminLoad = lazy(() => import('@screens/Admin/AdminLoad'));
const AdminUsers = lazy(() => import('@screens/Admin/Users'));
const AdminBakeStatus = lazy(() => import('@screens/Admin/BakeStatus'));
const AdminPerfChecklist = lazy(() => import('@screens/Admin/PerfChecklist'));
const AdminVerifyReports = lazy(() => import('@screens/Admin/VerifyReports'));
const AdminMinSpec = lazy(() => import('@screens/Admin/MinSpec'));
const AdminAiUsage = lazy(() => import('@screens/Admin/AiUsage'));
const AdminStoryRooms = lazy(() => import('@screens/Admin/StoryRooms'));
const AdminMediaAnalysis = lazy(() => import('@screens/Admin/MediaAnalysis'));
const AdminLayout = lazy(() => import('@screens/Admin/AdminLayout'));
// 웹 도감 — 인게임 도감과 «같은 컴포넌트»를 전체 화면으로 띄운다.
// lazy 라 로그인 화면에서는 이 번들을 안 받는다.
const Book = lazy(() => import('@screens/Book/Book'));
// 동행 라이트 화면 — 폰 알림을 누르면 열린다. 3D 게임 번들을 받지 않는다.
const Companion = lazy(() => import('@screens/Companion/Companion'));
const GameEntry = lazy(() => import('./GameEntry'));

/** SPA 밖(정적 HTML)으로 나가는 이동. `<Navigate>` 는 라우터 «안»에서만 도므로 못 쓴다. */
function HardRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to + window.location.search + window.location.hash);
  }, [to]);
  return null;
}

function AppFrame() {
  const location = useLocation();
  const isGameRoute = location.pathname === '/game';
  const appClassName = isGameRoute ? 'app app-game' : 'app app-auth';

  return (
    <div className={appClassName}>
      {/* 설치 안내 — 설치하면 저장이 영구가 되어 받아 둔 캐시가 안 버려진다(`pwaInstall`). */}
      <InstallBanner gameRoute={isGameRoute} />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />

          <Route path="/signup" element={<Signup />} />
          {/* 기술 스펙 — 서버(/api/tech/access, TECH_DOC_EMAILS)가 허락한 계정만 본다. */}
          <Route path="/tech" element={<TechGate key="tech" />} />
          <Route path="/tech-skill" element={<TechGate key="skill" mode="skill" />} />
          <Route path="/feedback" element={<Feedback />} />
          {/* 웹 도감 — 2026-08-30 부터 **React 화면이다.** 종전엔 정적 페이지
              (`front/public/book.html`, 바닐라 JS 1,871줄)였고, 같은 콘텐츠를 인게임과
              다른 언어로 두 번 구현하고 있었다(등급 색표는 손으로 베껴져 있었다).
              지금은 인게임 도감과 «같은 컴포넌트»(`CodexCore`)를 전체 화면으로 띄운다.
              ⚠ 옛 주소 `/book.html` 은 여기로 되돌려 보내는 표지판만 남았다.
              ⚠ 몬스터 GLB 를 돌려 보는 «자산 검수 랩»은 도감이 아니다 → `/lab3d`. */}
          <Route path="/book" element={<Book />} />
          <Route path="/companion" element={<Companion />} />
          {/* 자산 3D 랩 — 정적 페이지(`front/public/lab3d.html`)다. 개발 서버는 vite
              미들웨어(`cleanStaticUrls`)가 `/lab3d` → `/lab3d.html` 로 먼저 갈아 끼우므로
              여기까지 안 온다. **운영 nginx 에는 그 규칙이 없어서** SPA 폴백으로 떨어지고,
              그대로 두면 `*` 가 로그인으로 보낸다. 여기서 한 번 더 튕겨 준다.
              ⚠ 해시·질의문자열을 «그대로» 넘긴다(`#/monster/<키>?preset=head` 가 살아야 한다). */}
          <Route path="/lab3d" element={<HardRedirect to="/lab3d.html" />} />
          {/* 게이트는 화면 안에 있다(sg_is_admin + 서버 is_superuser). 라우트에서 막으면
              「운영자만」이 두 군데서 판정돼, 한쪽만 고친 날 조용히 갈린다. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminLoad />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="bake" element={<AdminBakeStatus />} />
            <Route path="perf" element={<AdminPerfChecklist />} />
            <Route path="reports" element={<AdminVerifyReports />} />
            <Route path="minspec" element={<AdminMinSpec />} />
            <Route path="ai-usage" element={<AdminAiUsage />} />
            <Route path="story" element={<AdminStoryRooms />} />
            <Route path="media-analysis" element={<AdminMediaAnalysis />} />
          </Route>
          {/* 설정 Provider 하나가 게임 설정 전부를 소유한다. 사본 Provider 를 두면
              인스턴스 둘이 서로의 저장을 덮어쓴다(GameSettingsContext.jsx 머리말). */}
          <Route
            path="/game"
            element={(
              <GameSettingsProvider>
                <GameEntry />
              </GameSettingsProvider>
            )}
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppFrame />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
